/**
 * Shadow Evidence Policy (Solicitudes tab — TV1-2377 slice 7)
 *
 * Answers ONE question per Sub-Goal on the open request: how many times has the
 * rule behind it actually fired since it entered shadow?
 *
 * ── How the rule is FOUND before promotion ──────────────────────────────────
 *
 * The rule is bound at APPROVAL by a server-side policy (D1), but the READMODEL
 * does not project that binding: `AutomationRequestsReadmodelType.ruleId` is
 * written only by `SubGoalPromotionRecorded`. Read literally, that leaves the row
 * with NO handle on the rule for an `approved`-but-not-yet-promoted Sub-Goal —
 * which is EXACTLY the state the seller is in while deciding whether to promote,
 * i.e. the state this whole panel exists to serve. The shadow window would be
 * blind precisely when it matters.
 *
 * So the id is DERIVED, with `ruleIdForSubGoal` — the same function the server
 * binds with, imported from the domain package rather than re-spelled here. The
 * recorded `ruleId` still wins when present (it is what actually happened); the
 * derivation is the fallback for the pre-promotion window. See
 * `teselly-domain/src/automation/subGoalRuleId.ts` for why sharing one definition
 * is the property that matters, and `subGoalRuleId.test.ts` for the pin.
 *
 * ── Why the answer is still sometimes "there is no rule" ────────────────────
 *
 * Deriving an id is not the same as finding a rule. The approval-time derivation
 * is allowed to REFUSE (unresolved binding, 0 or ≥2 command nodes, an ERP-gated
 * trigger on an account with no ERP) and that refusal currently only LOGS — so an
 * approved Sub-Goal may have no rule at all, ever. Because the id is derived
 * deterministically, the ABSENCE of a row at that id is now a real answer rather
 * than an artefact of not knowing where to look.
 *
 * `no-rule` is rendered as its OWN thing — never as "0 firings". "0 firings"
 * asserts that something is watching; here nothing is, and saying zero would be a
 * reassuring lie (TV1-2340 risk 4 in its nastiest form: not an empty log, but a
 * WRONG one).
 *
 * ── Where the window starts ─────────────────────────────────────────────────
 *
 * `shadowSince` comes off the RULE row, stamped at binding time, i.e. at
 * approval. The request's `submittedAt` is NOT the shadow window and must never
 * be substituted for it.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { AutomationRequestsReadmodelType } from '@bett3r-dev/teselly-domain';
import { ruleIdForSubGoal } from '@bett3r-dev/teselly-domain/automation/constants';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { DeclaredInputOption, DeclaredInputOptions, RequestsState, ShadowEvidence } from '../state';
import { RequestsCommand } from '../commands';
import { unfilledInputsForPromotion } from '../invariants';

/**
 * How many recent firings the panel lists — AND the ceiling on `firingCount` itself:
 * the query that fetches `executions` is capped at this `limit`, so `inWindow.length`
 * (below) can never exceed it. When a rule has actually fired MORE than this many times
 * inside the shadow window, `firingCount` reads exactly `RECENT_LIMIT`, not the true
 * count — the panel must render that case as "at least N", never as an exact number.
 */
export const RECENT_LIMIT = 10;

const isOption = ( option: unknown ): option is DeclaredInputOption =>
  !!option && typeof option === 'object' && 'value' in option;

/**
 * Narrows the by-id read's `declaredInputs` to the served shape. The domain schema
 * types `materializedOptions` as an array, but the server builds a record keyed by
 * option kind (`rules.readmodel.ts`, `materializeDeclaredInputs`), so the response is
 * read from `unknown` rather than trusted. An input without options is dropped: there
 * is nothing to choose from.
 */
const toDeclaredInputOptions = ( raw: unknown ): DeclaredInputOptions[] => {
  if ( !Array.isArray( raw )) return [];
  return raw.flatMap(( input: unknown ) => {
    if ( !input || typeof input !== 'object' ) return [];
    const { inputName, label, materializedOptions } = input as Record<string, unknown>;
    if ( typeof inputName !== 'string' ) return [];
    if ( !materializedOptions || typeof materializedOptions !== 'object' || Array.isArray( materializedOptions )) return [];
    const groups = Object.entries( materializedOptions as Record<string, unknown> ).reduce<Record<string, DeclaredInputOption[]>>(
      ( acc, [ kind, options ]) => {
        if ( Array.isArray( options )) acc[kind] = options.filter( isOption );
        return acc;
      },
      {}
    );
    return [{ inputName, ...( typeof label === 'string' ? { label } : {}), materializedOptions: groups }];
  });
};

/**
 * Reads one Sub-Goal's shadow evidence. Shared by this policy and
 * `recordInputValuesPolicy`, which re-reads after a save so the promote gate
 * re-evaluates against the rule row.
 *
 * The rule's options are read here too, beside the row they belong to, and only
 * when the request page will render a control: an `approved` Sub-Goal with an
 * unfilled Declared Input. That is the one `RulesById` read per Sub-Goal. It is the
 * query that materializes the options; the list query does not.
 */
export const resolveShadowEvidence = async (
  backend: ClientLibraryType,
  subGoal: AutomationRequestsReadmodelType
): Promise<ShadowEvidence> => {
  // The RECORDED id when promotion stamped one; otherwise the SAME derivation
  // the server bound with. Never a guess: `ruleIdForSubGoal` is one shared
  // definition, pinned byte-for-byte in the domain package.
  const ruleId = subGoal.ruleId || ruleIdForSubGoal( subGoal.id );

  const rules = await backend.queries.Rules({
    filter: [{ field: 'ruleId', op: 'eq', value: ruleId }],
    // `declaredInputsValues` feeds the promote gate (TV1-2453): without it every
    // declared input of the approved graph would read as unfilled.
    select: [ 'id', 'ruleId', 'mode', 'shadowSince', 'declaredInputsValues' ]
  });
  const rule = ( rules || [])[0];
  // The recorded id names no rule → nothing is watching. Say exactly that.
  if ( !rule ) return { status: 'no-rule' };

  const executions = await backend.queries.RuleExecutions({
    filter: [{ field: 'ruleId', op: 'eq', value: ruleId }],
    sort: [{ field: 'startedAt', direction: 'desc' }],
    limit: RECENT_LIMIT
  } as Parameters<ClientLibraryType['queries']['RuleExecutions']>[0]);

  const shadowSince = ( rule as { shadowSince?: string }).shadowSince;
  // Count only what happened INSIDE the shadow window (D1). A firing from
  // before the window is not evidence about this approval.
  const inWindow = ( executions || []).filter(
    ( e ) => !shadowSince || !( e as { startedAt?: string }).startedAt
      || ( e as { startedAt?: string }).startedAt! >= shadowSince
  );

  const observed: ShadowEvidence = {
    status: 'observed',
    ruleId,
    shadowSince,
    firingCount: inWindow.length,
    recent: inWindow,
    declaredInputsValues: rule.declaredInputsValues
  };

  if ( subGoal.status !== 'approved' || unfilledInputsForPromotion( subGoal, observed ).length === 0 ) {
    return observed;
  }
  try {
    const byId = await backend.queries.RulesById( ruleId );
    return { ...observed, declaredInputs: toDeclaredInputOptions( byId?.declaredInputs ) };
  } catch {
    // The options read failed: the evidence still stands, the page offers no
    // control and keeps the "falta completar" hint.
    return observed;
  }
};

export const createShadowEvidencePolicy = (
  backend: ClientLibraryType
): Policy<RequestsState, RequestsCommand> => {
  let closed = false;

  return {
    name: 'automation-requests-shadow-evidence',

    // React when the open request changes, or when its rows arrive.
    shouldReact: ( prev, next ) =>
      prev.ui.selectedRequestId !== next.ui.selectedRequestId
      || ( !!next.ui.selectedRequestId && prev.data.subGoals !== next.data.subGoals ),

    onStateChange: async ({ next, ctx }) => {
      const requestId = next.ui.selectedRequestId;
      if ( !requestId ) return;

      const subGoals = next.data.subGoals.filter(( sg ) => sg.requestId === requestId );

      for ( const subGoal of subGoals ) {
        if ( closed ) return;
        // Only a reviewed Sub-Goal can have a rule at all. A proposed/composed
        // one is still waiting on a human — asking about its firings is asking
        // about something that cannot exist yet.
        if ( subGoal.status !== 'approved' && subGoal.status !== 'live' ) continue;

        const evidence: ShadowEvidence = await resolveShadowEvidence( backend, subGoal );

        if ( closed ) return;
        ctx.dispatch(
          { type: 'automation/requests/data/setEvidence', payload: { subGoalId: subGoal.id, evidence }},
          { source: 'api' }
        );
      }
    },

    cleanup: () => {
      closed = true;
    }
  };
};
